<template>
  <a-form-item
    :label="props.label"
    :name="props.name"
    :rules="[
      {
        required: props.required,
        message: props.message,
      },
    ]"
  >
    <a-select
      class="ant-select-multi-tags"
      show-search
      v-model:value="value"
      :placeholder="props.placeholder"
      :options="options"
      :disabled="props.disabled"
      :filter-option="filterOption"
      :loading="loading"
      @change="handleChange"
    />
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </a-form-item>
</template>

<script setup lang="ts">
import { getPrefix } from "@/apis/register/get";
import type { SelectProps } from "ant-design-vue";
import _ from "lodash";
import { computed, ref, watchEffect } from "vue";
import type { PrefixNameProps } from "./type";
import { Prefix } from "@/apis/register/type";

const props = withDefaults(defineProps<PrefixNameProps>(), {
  placeholder: "กรุณาเลือก",
  disabled: false,
  message: "",
});

const emit = defineEmits(["update:value", "update:id"]);

const value = computed({
  get: () => props.value,
  set: (value) => emit("update:value", value),
});

const search = ref<string>("");
const options = ref<SelectProps["options"]>([]);
const loading = ref<boolean>(false);
const errorMessage = ref<string>("");

const excludedValues = [
  "ดร.",
  "นายแพทย์",
  "แพทย์หญิง",
  "ทัณตแพทย์",
  "ทัณตแพทย์หญิง",
  "สัตวแพทย์",
  "สัตวแพทย์หญิง",
  "เภสัชกรชาย",
  "เภสัชกรหญิง",
] as const;

const fetchPrefix = _.debounce(async (keyword: string = "") => {
  try {
    loading.value = true;
    errorMessage.value = "";
    const { status, data } = await getPrefix({ keyword });
    if (status === 200 && data?.length) {
      const filteredOptions = data.filter(
        (el) =>
          !(excludedValues as readonly string[]).includes(el.value as string),
      );
      options.value = filteredOptions;
    }
  } catch (error) {
    errorMessage.value = "Failed to fetch prefix options. Please try again.";
    console.error("Failed to fetch prefix options:", error);
  } finally {
    loading.value = false;
  }
}, 500);

watchEffect(() => {
  fetchPrefix(search.value);
});

function filterOption(input: string, option: { value: string }) {
  return option.value.toLowerCase().includes(input.toLowerCase());
}

const handleChange = (value: string, option: Prefix) => {
  if (option && option.id) {
    emit("update:id", option.id);
  } else {
    console.error("Option or Option ID is missing:", option);
  }
};
</script>

<style scoped>
.error-message {
  color: red;
  margin-top: 8px;
}
</style>
